import version from '@/utils/version';

export class EventsApi {
  private readonly queue: (() => Promise<Response>)[] = [];

  private readonly FLUSH_INTERVAL = 1000;

  private flushed = false;

  private intervalId: number | NodeJS.Timeout;

  private userId: string;
  private apiUrl: string;

  init(userId, apiUrl) {
    this.userId = userId;
    this.apiUrl = apiUrl;
    this.intervalId = setInterval(() => {
      if (this.queue.length || !this.flushed) {
        this.flush();
      }
    }, this.FLUSH_INTERVAL);
  }

  isReady() {
    return !!this.userId && !!this.apiUrl && !!this.intervalId;
  }

  identify(traits: Record<string, unknown> = {}): void {
    this.queue.push(() =>
      fetch(`${this.apiUrl}/events/identify`, {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({
          userId: this.userId,
          traits: {
            // eslint-disable-next-line camelcase
            sdk_version: version,
            source: 'chat_sdk',
            ...traits,
          },
        }),
      })
    );

    this.flushed = false;
  }

  track(event: EVENTS, properties: Record<string, unknown> = {}): void {
    this.queue.push(() =>
      fetch(`${this.apiUrl}/events/track`, {
        method: 'POST',
        headers: this.getDefaultHeaders(),
        body: JSON.stringify({
          userId: this.userId,
          event,
          properties,
        }),
      })
    );

    this.flushed = false;
  }

  private flush(): void {
    this.flushed = true;
    while (this.queue.length) {
      const req = this.queue.shift();
      if (req) {
        req().catch(() => {
          return;
        });
      }
    }
  }

  private getDefaultHeaders() {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'User-Agent': `Naluri Chat SDK /JS (${version}})`,
      'x-nl-sdk-version': version,
    };
  }

  cleanup(): void {
    this.flush();
    clearInterval(this.intervalId);
  }
}

export const eventsApi = new EventsApi();

export const EVENTS = {
  LOGIN: 'login',
  REFRESH: 'refresh',
  LOGOUT: 'logout',
  SEND_TEXT_MESSAGE: 'send_text_message',
  SEND_IMAGE_MESSAGE: 'send_image_message',
  SEND_FILE_MESSAGE: 'send_file_message',
  GET_CHANNEL_MESSAGES: 'get_channel_messages',
  GET_CHANNELS: 'get_channels',
  GET_CHANNEL: 'get_channel',
  GET_MESSAGE: 'get_message',
  GET_COACH_PROFILE: 'get_coach_profile',
  GET_COACHES: 'get_coaches',
  MARK_MESSAGE_AS_READ: 'mark_message_as_read',

  // errors
  LOGIN_ERROR: 'login_error',
  LOGOUT_ERROR: 'logout_error',
  REFRESH_ERROR: 'refresh_error',
  SEND_TEXT_MESSAGE_ERROR: 'send_text_message_error',
  SEND_IMAGE_MESSAGE_ERROR: 'send_image_message_error',
  SEND_FILE_MESSAGE_ERROR: 'send_file_message_error',
  GET_CHANNEL_MESSAGES_ERROR: 'get_channel_messages_error',
  GET_CHANNELS_ERROR: 'get_channels_error',
  GET_CHANNEL_ERROR: 'get_channel_error',
  GET_MESSAGE_ERROR: 'get_message_error',
  GET_COACH_PROFILE_ERROR: 'get_coach_profile_error',
  GET_COACHES_ERROR: 'get_coaches_error',
  CHANNEL_SUBSCRIPTION_ERROR: 'channel_subscription_error',
  CHANNEL_LISTING_SUBSCRIPTION_ERROR: 'channel_listing_subscription_error',
  CHANNEL_ACTING_COACH_INJECTION_ERROR: 'channel_acting_coach_injection_error',
} as const;

export type EVENTS = (typeof EVENTS)[keyof typeof EVENTS];
